/**
 * One BAM IQ conversation: the messages, the answer being written, and the
 * box to ask in. Used by both the side panel and the #/iq page.
 *
 * What the coach can always tell from the screen:
 *   - what's happening while they wait (searching the library, which sources
 *     it's reading, thinking, writing), and a way to stop;
 *   - whose words each source is (Coleman's book, from BAM, AI summary, web),
 *     and where tapping it goes: numbered markers in the text, a hover card,
 *     and a strip of sources under the answer;
 *   - that nothing is saved unless they tap it ("Remember this?"), and when an
 *     answer used what BAM IQ remembers about them;
 *   - that BAM IQ is an AI assistant, not Coleman (safety policy D73), and
 *     that it sees a plan only when they attach it and saves nothing itself.
 *
 * Keys: Enter sends, Shift+Enter is a new line, the up arrow in an empty box
 * brings back the last question. The mic (where the browser offers speech
 * recognition) types what the coach says into the box; nothing is sent until
 * they send it.
 */
import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, Copy, Check, MessageSquare, Mic, Square, BookOpen, PlayCircle, Globe, FileText } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { openRef, canOpen, sourceLabel } from "./refs";
import { renderMarkdown } from "./markdown";
import { useSmoothText, handoff } from "./smoothText";
import { stageText } from "./useBamIQ";
import { AttachBar, ConsentCard, ProposalCard, chipText } from "./BamIQAttach";
import { FEEDBACK_REASONS, NOTE_MAX, OVERALL } from "./feedback";

const ff = "'DM Sans', sans-serif";
const EXAMPLES = [
  "My guards panic against full-court pressure. What should I do?",
  "What's a drag screen?",
  "I coach 12-year-olds and they look great in drills but lost in games. Ideas for tomorrow?",
];
export const FOLLOW_UPS = { shorter: "Shorter, please.", deeper: "Go deeper on that." };
const isId = (x) => typeof x === "string" && /^[0-9a-f-]{36}$/i.test(x);
const tagNumber = (tag) => String(tag || "").replace(/^S/, "");
export const thumbUrl = (id) => (id ? `https://image.mux.com/${id}/thumbnail.jpg?width=320&height=180&fit_mode=smartcrop` : null);
/** An answer as plain text, for Copy: the source markers come out. */
export const plainAnswer = (body) => String(body || "").replace(/\s?\[S\d{1,3}\]/g, "")
  .replace(/^[ \t]*:::[ \t]*try\b[ \t]*(.*)$/gim, "Try this: $1").replace(/^[ \t]*:::[ \t]*$/gm, "").replace(/\n{3,}/g, "\n\n").trim();

const TypeIcon = ({ r, size = 14, color }) => {
  const I = r?.type === "web" || r?.type === "external" ? Globe : r?.type === "book" ? BookOpen : r?.type === "weekly_qa" ? FileText : PlayCircle;
  return <I size={size} color={color}/>;
};

/* ── citations ─────────────────────────────────────────────────────────── */

/** The card shown over a marker on hover or focus: picture, title, whose words. */
function PreviewCard({ r, at, C }) {
  if (!r || !at) return null;
  const w = 260;
  const left = Math.max(8, Math.min(at.left - 20, window.innerWidth - w - 8));
  const above = at.top > 220;
  return (
    <div role="tooltip" style={{ position: "fixed", zIndex: 6000, left, width: w, ...(above ? { bottom: window.innerHeight - at.top + 8 } : { top: at.bottom + 8 }),
      borderRadius: 12, overflow: "hidden", background: C.bgCard, border: `1px solid ${C.border}`, boxShadow: C.elevatedShadow, fontFamily: ff, pointerEvents: "none" }}>
      {r.thumb && <img src={thumbUrl(r.thumb)} alt="" style={{ display: "block", width: "100%", aspectRatio: "16 / 9", objectFit: "cover", background: C.bgHover }}/>}
      <div style={{ padding: "9px 11px", display: "grid", gap: 3 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.35 }}>{r.title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.textMid }}>
          <TypeIcon r={r} size={12} color={C.textMid}/> {sourceLabel(r)}{canOpen(r) ? " · tap to open" : ""}
        </div>
      </div>
    </div>
  );
}

function Cite({ tag, refs, go, C }) {
  const r = (refs || []).find(x => x.tag === tag);
  const [at, setAt] = useState(null);
  if (!r) return null;
  const openable = canOpen(r);
  const show = (e) => setAt(e.currentTarget.getBoundingClientRect());
  return (
    <>
      <button type="button" className="btn-inline" data-cite={tag} aria-label={`Source ${tagNumber(tag)}: ${r.title}, ${sourceLabel(r)}`}
        onClick={() => openable && openRef(r, go)} onMouseEnter={show} onMouseLeave={() => setAt(null)} onFocus={show} onBlur={() => setAt(null)}
        style={{ display: "inline-grid", placeItems: "center", minWidth: 17, height: 17, padding: "0 4px", margin: "0 2px", borderRadius: 999, border: "none",
          background: `${GOLD}2E`, color: C.text, fontSize: 10.5, fontWeight: 800, fontFamily: ff, lineHeight: 1, verticalAlign: "2px",
          cursor: openable ? "pointer" : "default" }}>
        {tagNumber(tag)}
      </button>
      {at && <PreviewCard r={r} at={at} C={C}/>}
    </>
  );
}

/**
 * What it's reading, as one quiet line (Coleman, 2026-11-01: "it shouldn't emphasize the exact videos it's
 * searching... as much"): "Looking through 5 videos and a book from BAM's library". The sources themselves show under
 * the answer once it's written.
 */
export function readingLine(sources = []) {
  const KIND = { book: ["book", "books"], doc: ["book", "books"], weekly_qa: ["Q&A", "Q&As"], instagram: ["post", "posts"], external: ["post", "posts"] };
  const counts = new Map();
  for (const s of sources || []) {
    const k = KIND[s?.type] || (s?.type === "podcast_summary" ? ["podcast note", "podcast notes"] : ["video", "videos"]);
    counts.set(k[0], { k, n: (counts.get(k[0])?.n || 0) + 1 });
  }
  const parts = [...counts.values()].sort((a, b) => b.n - a.n).map(({ k, n }) => (n === 1 ? `${/^[aeiou]/i.test(k[0]) ? "an" : "a"} ${k[0]}` : `${n} ${k[1]}`));
  if (!parts.length) return "";
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `Looking through ${list} from BAM's library`;
}

/** Every source under the answer, with its picture where it has one. */
function SourceStrip({ refs, go, C }) {
  if (!refs || !refs.length) return null;
  return (
    <div style={{ display: "flex", gap: 8, overflowX: "auto", margin: "12px 0 0", paddingBottom: 4, scrollbarWidth: "thin" }}>
      {refs.map((r, i) => {
        const openable = canOpen(r);
        return (
          <button key={`${r.tag || "w"}-${i}`} type="button" data-source={r.tag || "web"} data-type={r.type || ""} disabled={!openable} onClick={() => openRef(r, go)}
            style={{ flex: "0 0 auto", width: 168, display: "grid", gridTemplateRows: "auto 1fr", padding: 0, borderRadius: 10, overflow: "hidden",
              border: `1px solid ${C.border}`, background: C.bgHover, color: C.text, fontFamily: ff, textAlign: "left", cursor: openable ? "pointer" : "default" }}>
            {/* A fixed box the picture fills, so a tall thumbnail can't spill over the title (2026-11-01). */}
            <div style={{ position: "relative", height: 64, overflow: "hidden", background: C.bg, display: "grid", placeItems: "center" }}>
              {r.thumb ? <img src={thumbUrl(r.thumb)} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}/> : <TypeIcon r={r} size={20} color={C.textDim}/>}
              {r.tag && <span style={{ position: "absolute", top: 6, left: 6, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, background: GOLD, color: "#111",
                fontSize: 10.5, fontWeight: 800, display: "grid", placeItems: "center" }}>{tagNumber(r.tag)}</span>}
            </div>
            <div style={{ padding: "7px 9px 8px", display: "grid", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{r.title || sourceLabel(r)}</span>
              <span style={{ fontSize: 11, color: C.textMid }}>{sourceLabel(r)}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

const playerNameFor = (iq, playerId) => (iq.attachments || []).find(a => a.type === "player" && a.id === playerId)?.title
  || (iq.players || []).find(p => p.id === playerId)?.name || null;

function CoachBubble({ body, attachments = [], C }) {
  return (
    <div style={{ alignSelf: "flex-end", maxWidth: "85%", display: "grid", gap: 4, justifyItems: "end" }}>
      {attachments.length > 0 && (
        <div style={{ fontSize: 11.5, color: C.textMid }}>Attached: {attachments.map(chipText).join(", ")}</div>
      )}
      <div style={{ padding: "9px 14px", borderRadius: "16px 16px 4px 16px", background: C.bgHover, border: `1px solid ${C.border}`,
        color: C.text, fontSize: 14.5, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{body}</div>
    </div>
  );
}

/** A "try this" card: a game or activity to run, set apart from the explanation. */
function TryCard({ title, children, C }) {
  return (
    <div data-try style={{ margin: "4px 0 14px", padding: "12px 14px 2px", borderRadius: 12, border: `1px solid ${C.border}`, borderLeft: `3px solid ${GOLD}`, background: C.bgHover }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", color: C.textMid, marginBottom: 3 }}>Try this</div>
      {title && <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{title}</div>}
      <div style={{ fontSize: 14 }}>{children}</div>
    </div>
  );
}
const cardRenderer = (C) => (title, children, key) => <TryCard key={key} title={title} C={C}>{children}</TryCard>;
/** While an answer streams (or after it's cut off), its sources aren't checked yet: plain numbers, not links. */
const plainTag = (C) => (tag, key) => <span key={key} style={{ fontSize: 10.5, fontWeight: 800, color: C.textMid, margin: "0 2px", verticalAlign: "2px" }}>{tagNumber(tag)}</span>;

// Reading: a comfortable measure, quiet headings, gold kept for sources and actions.
const READING = {
  paragraph: { margin: "0 0 10px", lineHeight: 1.6 },
  heading: { fontWeight: 700, fontSize: 15, margin: "18px 0 6px", letterSpacing: ".005em" },
  list: { margin: "0 0 12px", paddingLeft: 20, lineHeight: 1.6 },
  item: { margin: "4px 0" },
};

/* ── answer actions ────────────────────────────────────────────────────── */

function ActionBtn({ label, onClick, active, C, children }) {
  return (
    <button type="button" aria-label={label} title={label} aria-pressed={active ?? undefined} onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 28, padding: "0 8px", borderRadius: 7, border: "none",
        background: active ? `${GOLD}24` : "transparent", color: active ? C.text : C.textMid, fontFamily: ff, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
      {children}
    </button>
  );
}

/** Founder only (D154): rate the answer, say what was off, and turn it into a test case. */
function FounderFeedback({ m, iq, C, open, setOpen }) {
  const fb = m.feedback || null;
  const [reasons, setReasons] = useState(fb?.reasons || []);
  // The overall call is optional (2026-11-01: "it could be both/indifferent"): Good, Mixed, Off, or none.
  const [overall, setOverall] = useState(fb && fb.rating !== "note" ? fb.rating : null);
  const [note, setNote] = useState(fb?.note || "");
  const [caseState, setCaseState] = useState(null);   // null | "saving" | "saved" | error text
  // Talk instead of typing (Coleman, 2026-10-26: "I have a decent amount of thoughts, many of which are like little
  // suggestions, but it makes it a lot easier if I can talk into it and have it transcribed"): the question box's mic.
  const dictation = useDictation(setNote);
  const toggle = (r) => setReasons(cur => (cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r]));
  const save = async (e) => {
    e?.preventDefault?.();
    if (dictation.listening) dictation.stop();
    await iq.feedback(m.id, overall || "note", reasons, note.trim() || null);
    setOpen(false);
  };
  // One tap for the usual path (2026-10-25: "I should mainly need to rate the answer, identify what's wrong or good, and
  // write a short note"): save it and make the case, whose fields are drafted for him to check.
  const saveAndCase = async () => { await save(); await makeCase(); };
  const makeCase = async () => {
    setCaseState("saving");
    try { await iq.makeCase(m.id); setCaseState("saved"); } catch (err) { setCaseState(err.message || "Couldn't make it."); }
  };
  return (
    <>
      {open && (
        <form onSubmit={save} data-founder-feedback
          style={{ display: "grid", gap: 8, marginTop: 6, padding: 10, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 12, color: C.textMid }}>
          <div role="group" aria-label="Overall" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <span>Overall <span style={{ opacity: .7 }}>(optional)</span></span>
            {OVERALL.map(([k, l]) => (
              <button key={k} type="button" aria-pressed={overall === k} onClick={() => setOverall(o => (o === k ? null : k))}
                style={{ padding: "3px 10px", borderRadius: 999, border: `1px solid ${overall === k ? GOLD : C.border}`, background: overall === k ? `${GOLD}22` : "transparent",
                  color: C.text, fontFamily: ff, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{l}</button>
            ))}
          </div>
          <div role="group" aria-label="What stood out?" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <span>What stood out?</span>
            {FEEDBACK_REASONS.map(r => (
              <button key={r} type="button" aria-pressed={reasons.includes(r)} onClick={() => toggle(r)}
                style={{ padding: "3px 9px", borderRadius: 999, border: `1px solid ${reasons.includes(r) ? GOLD : C.border}`, background: reasons.includes(r) ? `${GOLD}22` : "transparent",
                  color: C.text, fontFamily: ff, fontSize: 12, cursor: "pointer" }}>{r}</button>
            ))}
          </div>
          <div style={{ position: "relative" }}>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={note.length > 200 ? 7 : 3} maxLength={NOTE_MAX} aria-label="What would you have preferred?"
              placeholder={dictation.listening ? "Listening… say everything, every little suggestion" : "What was good, what was off, what you'd have wanted (type, or press the mic and talk)"}
              style={{ width: "100%", boxSizing: "border-box", resize: "vertical", padding: "7px 40px 7px 9px", borderRadius: 8, border: `1px solid ${dictation.listening ? "#E57373" : C.border}`, background: C.inputBg, color: C.text, fontFamily: ff, fontSize: 13 }}/>
            {dictation.supported && (
              <button type="button" aria-label={dictation.listening ? "Stop dictating" : "Dictate your feedback"} aria-pressed={dictation.listening}
                title={dictation.listening ? "Stop" : "Talk: your browser turns what you say into text"}
                onClick={() => (dictation.listening ? dictation.stop() : dictation.start(note))}
                className={dictation.listening ? "bamiq-listening" : undefined}
                style={{ position: "absolute", right: 6, top: 6, width: 28, height: 28, borderRadius: 999, border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: dictation.listening ? "#E57373" : "transparent", color: dictation.listening ? "#111" : C.textMid }}>
                {dictation.listening ? <Square size={11} fill="#111"/> : <Mic size={15}/>}
              </button>
            )}
          </div>
          {dictation.error && <div role="alert" style={{ fontSize: 11.5, color: "#E57373" }}>{dictation.error}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={saveAndCase} disabled={caseState === "saving"} style={{ ...btn(C, true), padding: "5px 11px", fontSize: 12 }}>{caseState === "saving" ? "Making the case…" : "Save and make a test case"}</button>
            <button type="submit" style={{ ...btn(C, false), padding: "5px 11px", fontSize: 12 }}>Just save</button>
            <span style={{ fontSize: 11.5 }}>Founder only. Saved with this answer and what produced it (model, sources, cost).</span>
          </div>
        </form>
      )}
      {!open && fb && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 4, fontSize: 11.5, color: C.textMid }}>
          <span>Saved{fb.rating && fb.rating !== "note" ? ` (${OVERALL.find(([k]) => k === fb.rating)?.[1].toLowerCase()})` : ""}{fb.reasons?.length ? `: ${fb.reasons.join(", ").toLowerCase()}` : ""}.</span>
          <button type="button" className="btn-inline" onClick={() => setOpen(true)} style={linkStyle(C)}>Edit</button>
          {caseState === "saved" ? <span data-case-saved>Test case saved. Review it under Test cases on the BAM IQ page.</span>
            : <button type="button" className="btn-inline" onClick={makeCase} disabled={caseState === "saving"} style={linkStyle(C)}>
                {caseState === "saving" ? "Making a test case…" : "Make a test case"}</button>}
          {caseState && !["saving", "saved"].includes(caseState) && <span role="alert">{caseState}</span>}
        </div>
      )}
    </>
  );
}
const linkStyle = (C) => ({ border: "none", background: "transparent", padding: 0, color: C.textMid, fontFamily: ff, fontSize: 11.5, textDecoration: "underline", cursor: "pointer" });

function AnswerActions({ m, iq, latest, C }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const fb = m.feedback?.rating || null;
  const copy = async () => {
    const text = plainAnswer(m.body);
    try { await navigator.clipboard.writeText(text); } catch {
      const t = document.createElement("textarea"); t.value = text; document.body.appendChild(t); t.select();
      try { document.execCommand("copy"); } catch { /* nothing more to try */ } t.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };
  // One Feedback button, not thumbs (2026-11-01: an answer can be good and off at once, or neither): it opens the box,
  // where the overall call is optional and the mic is the main way in.
  const rating = iq.founder && isId(m.id) && iq.feedback;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, marginLeft: -8 }}>
        <ActionBtn label={copied ? "Copied" : "Copy"} onClick={copy} C={C}>{copied ? <Check size={14}/> : <Copy size={14}/>}{copied ? "Copied" : "Copy"}</ActionBtn>
        {latest && !iq.pending && (
          <>
            <ActionBtn label="Shorter" onClick={() => iq.send(FOLLOW_UPS.shorter)} C={C}>Shorter</ActionBtn>
            <ActionBtn label="Go deeper" onClick={() => iq.send(FOLLOW_UPS.deeper)} C={C}>Go deeper</ActionBtn>
          </>
        )}
        {rating && (
          <>
            <span style={{ flex: 1 }}/>
            <ActionBtn label="Feedback" active={open || !!fb} onClick={() => setOpen(o => !o)} C={C}><MessageSquare size={14}/>Feedback</ActionBtn>
          </>
        )}
      </div>
      {rating && <FounderFeedback m={m} iq={iq} C={C} open={open} setOpen={setOpen}/>}
    </div>
  );
}

// Answers already written out once, so going back to a thread never replays them.
const written = new Set();

/** The answer as it streams in, at an even pace (smoothText.js). */
function StreamText({ text, C }) {
  const { text: shown } = useSmoothText(text, { track: true });
  return shown ? <div className="bamiq-stream">{renderMarkdown(shown, { styles: READING, renderTag: plainTag(C), renderCard: cardRenderer(C) })}</div> : null;
}

function Answer({ m, go, C, onRemember, onDismiss, iq, latest, onShowMemory }) {
  // The answer that just finished carries on from where the stream was on screen; any other shows whole.
  const [from] = React.useState(() => (m.fresh && !written.has(m.id) ? handoff.len : Infinity));
  const { text: body, done } = useSmoothText(m.body, { from });
  React.useEffect(() => { if (done) written.add(m.id); }, [done, m.id]);
  const md = renderMarkdown(body, { renderTag: (tag, key) => <Cite key={key} tag={tag} refs={m.refs} go={go} C={C}/>, renderCard: cardRenderer(C), styles: READING });
  return (
    <div data-answer style={{ fontSize: 15, color: C.text, minWidth: 0, maxWidth: "65ch", overflowWrap: "anywhere" }}>
      {m.emergency && <Emergency text={m.emergency} C={C}/>}
      {md}
      {done && (m.notices || []).map((n, i) => <div key={i} style={{ fontSize: 12.5, color: C.textMid, fontStyle: "italic", marginTop: 4 }}>{n}</div>)}
      {done && <SourceStrip refs={m.refs} go={go} C={C}/>}
      {(m.proposals || []).map(p => <ProposalCard key={p.action_id} proposal={p} messageId={m.id} iq={iq} C={C} playerName={playerNameFor(iq, p.player_id)}/>)}
      {m.remember && (
        <div role="group" aria-label="Remember this?" style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`,
          background: C.bgHover, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 13 }}>
          <span style={{ flex: "1 1 220px", minWidth: 0 }}>Remember for next time? <strong>{m.remember.label}: {m.remember.value}</strong></span>
          <button type="button" onClick={() => onRemember(m.id, m.remember)} style={btn(C, true)}>Remember</button>
          <button type="button" onClick={() => onDismiss(m.id)} style={btn(C, false)}>Not now</button>
        </div>
      )}
      {m.remembered && <div style={{ marginTop: 8, fontSize: 12.5, color: C.green }}>Saved to what BAM IQ remembers: {m.remembered.value}</div>}
      {m.profile_used && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: C.textMid }}>
          Using what BAM IQ knows about you{onShowMemory && <> · <button type="button" className="btn-inline" onClick={onShowMemory}
            style={{ border: "none", background: "transparent", padding: 0, color: C.textMid, fontFamily: ff, fontSize: 11.5, textDecoration: "underline", cursor: "pointer" }}>See or edit</button></>}
        </div>
      )}
      <AnswerActions m={m} iq={iq} latest={latest} C={C}/>
    </div>
  );
}

function Emergency({ text, C }) {
  return (
    <div role="alert" style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 10, border: "1px solid #E5737388",
      background: "rgba(229,115,115,0.12)", color: C.text, fontWeight: 700, fontSize: 14 }}>{text}</div>
  );
}

const btn = (C, primary) => ({
  padding: "7px 12px", borderRadius: 8, fontFamily: ff, fontSize: 13, fontWeight: 800, cursor: "pointer",
  border: primary ? "none" : `1px solid ${C.border}`, background: primary ? GOLD : "transparent", color: primary ? "#111" : C.text,
});

/* ── dictation ─────────────────────────────────────────────────────────── */

const Recognition = () => (typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition || null : null);

/**
 * The browser's own speech recognition, typing into the box. Off where the browser doesn't offer it.
 * Chrome ends a session on each short silence; we fold its words into the text so far and start a
 * fresh one, so a pause mid-thought doesn't stop you (until you tap stop). A permission or hardware
 * problem won't fix itself: we stop and return a reason to show, rather than failing silently.
 */
function useDictation(setDraft) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const rec = useRef(null);
  const want = useRef(false);   // the coach means to be dictating (tapped the mic, hasn't stopped)
  const base = useRef("");      // words committed from sessions that already ended
  const fails = useRef(0);      // sessions that ended at once with nothing: a stuck mic, not a pause
  const supported = !!Recognition();
  useEffect(() => () => { want.current = false; try { rec.current?.abort(); } catch { /* already stopped */ } }, []);
  const stop = () => { want.current = false; try { rec.current?.stop(); } catch { /* already stopped */ } };
  const run = () => {
    const SR = Recognition();
    if (!SR) return false;
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = navigator.language || "en-US";
    const startedAt = Date.now();
    let said = "";
    r.onresult = (e) => {
      said = "";
      for (let i = 0; i < e.results.length; i++) said += e.results[i][0].transcript;
      setDraft((base.current + said).replace(/^\s+/, ""));
    };
    r.onerror = (e) => {
      if (["not-allowed", "service-not-allowed", "audio-capture"].includes(e?.error)) {
        want.current = false;
        setError(e.error === "audio-capture" ? "No microphone found." : "Microphone access is blocked. Allow it for this site, then try again.");
      }
      // no-speech, aborted and network fall through to onend, which starts a fresh session if we still want to.
    };
    r.onend = () => {
      if (said) { base.current = `${(base.current + said).replace(/\s+$/, "")} `; fails.current = 0; }
      else if (Date.now() - startedAt < 500) fails.current += 1;
      if (want.current && fails.current < 5 && run()) return;   // keep going across the silence
      want.current = false;
      setListening(false);
    };
    rec.current = r;
    try { r.start(); return true; } catch { return false; }
  };
  const start = (current) => {
    setError(null); fails.current = 0;
    base.current = current ? `${current.replace(/\s+$/, "")} ` : "";
    want.current = true;
    if (run()) setListening(true); else { want.current = false; setListening(false); }
  };
  return { supported, listening, error, start, stop };
}

/* ── usage ─────────────────────────────────────────────────────────────── */

const resetDay = (iso) => {
  try { return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return "the 1st"; }
};

function UsageLine({ usage, C }) {
  if (!usage || !(usage.used >= 0.75) || usage.used >= 1) return null;
  return (
    <div data-usage style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 11.5, color: C.textMid }}>
      <div aria-hidden="true" style={{ flex: "0 0 56px", height: 4, borderRadius: 2, background: C.border, overflow: "hidden" }}>
        <div style={{ width: `${Math.round(usage.used * 100)}%`, height: "100%", background: GOLD }}/>
      </div>
      You've used {Math.round(usage.used * 100)}% of this month's BAM IQ. It resets {resetDay(usage.resets)}.
    </div>
  );
}

function LimitCard({ usage, C }) {
  return (
    <div data-limit role="status" style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgHover, display: "grid", gap: 4 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>That's this month's BAM IQ.</div>
      <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5 }}>
        You've used your monthly allowance. It resets {usage?.resets ? resetDay(usage.resets) : "on the 1st"}. Every conversation is still here to read.
      </div>
    </div>
  );
}

/* ── the conversation ──────────────────────────────────────────────────── */

export default function BamIQConversation({ iq, go, C, compact = false, page = null, autoFocus = false, onShowMemory = null, pageThread = null }) {
  const [draft, setDraft] = useState("");
  const [away, setAway] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const nearBottom = useRef(true);
  const { messages, pending, error, interrupted, loading } = iq;
  const dictation = useDictation(setDraft);
  const atLimit = (iq.usage?.used ?? 0) >= 1;
  const lastQuestion = [...messages].reverse().find(m => m.role === "coach")?.body || "";
  const lastAnswerId = [...messages].reverse().find(m => m.role === "assistant")?.id;

  useEffect(() => {
    const el = listRef.current;
    if (el && nearBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages, pending, interrupted]);
  useEffect(() => { if (autoFocus && !pending) inputRef.current?.focus(); }, [autoFocus, pending, iq.threadId]);

  const autosize = (el) => { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(160, el.scrollHeight) + "px"; };
  useEffect(() => { autosize(inputRef.current); }, [draft]);

  const submit = (text) => {
    const t = (text ?? draft).trim();
    if (!t || pending || atLimit) return;
    if (dictation.listening) dictation.stop();
    setDraft("");
    nearBottom.current = true; setAway(false);
    iq.send(t);
  };
  const jump = () => {
    const el = listRef.current;
    nearBottom.current = true; setAway(false);
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };
  const empty = !messages.length && !pending && !interrupted && !iq.consentAsk && !loading;
  const known = iq.memory?.enabled ? (iq.memory.items || []) : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, fontFamily: ff, position: "relative" }}>
      <div ref={listRef} onScroll={(e) => { const el = e.currentTarget; const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80; nearBottom.current = near; setAway(!near); }}
        style={{ flex: 1, overflowY: "auto", padding: compact ? "18px 18px 8px" : "28px 28px 8px", display: "flex", flexDirection: "column", gap: 22 }}>
        {empty && (
          <div data-empty style={{ display: "grid", gap: 14, maxWidth: 560, marginTop: compact ? 4 : 24 }}>
            <img src={BAM_LOGO_PNG} alt="" width={30} height={36} style={{ opacity: 0.95 }}/>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, lineHeight: 1, letterSpacing: ".02em", color: C.text }}>What are you working on?</div>
            <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.55 }}>Ask about a problem you're seeing, a drill, a tactic, or an idea you want to test. BAM IQ thinks with deep basketball and coaching knowledge, pulls from BAM's library and checks live sources when they help, and tells you whose words each answer is using.</div>
            {known.length > 0 && (
              <div style={{ fontSize: 12.5, color: C.textMid }}>
                Tuned to you: {known.slice(0, 4).map(i => i.value).join(" · ")}
                {onShowMemory && <> · <button type="button" className="btn-inline" onClick={onShowMemory} style={{ border: "none", background: "transparent", padding: 0, color: C.textMid, fontFamily: ff, fontSize: 12.5, textDecoration: "underline", cursor: "pointer" }}>Edit</button></>}
              </div>
            )}
            {pageThread && (
              <button type="button" onClick={() => iq.openThread(pageThread.id)} data-page-thread
                style={{ textAlign: "left", padding: "9px 12px", borderRadius: 10, border: `1px dashed ${C.border}`, background: "transparent", color: C.text, fontFamily: ff, fontSize: 13, cursor: "pointer" }}>
                <span style={{ color: C.textMid }}>You asked about this before: </span>{pageThread.title || "Conversation"}
              </button>
            )}
            <div style={{ display: "grid", gap: 8 }}>
              {EXAMPLES.map(x => (
                <button key={x} type="button" onClick={() => submit(x)} disabled={atLimit}
                  style={{ textAlign: "left", padding: "10px 13px", borderRadius: 12, border: `1px solid ${C.border}`, background: "transparent",
                    color: C.text, fontFamily: ff, fontSize: 13.5, lineHeight: 1.45, cursor: atLimit ? "default" : "pointer" }}>{x}</button>
              ))}
            </div>
          </div>
        )}
        {loading && <div style={{ color: C.textMid, fontSize: 13 }}>Loading the conversation…</div>}
        {messages.map(m => (m.role === "coach"
          ? <CoachBubble key={m.id} body={m.body} attachments={m.attachments} C={C}/>
          : <Answer key={m.id} m={m} go={go} C={C} iq={iq} latest={m.id === lastAnswerId} onShowMemory={onShowMemory} onRemember={iq.remember} onDismiss={iq.dismissRemember}/>))}
        {pending && (
          <>
            <CoachBubble body={pending.coach} attachments={pending.attachments} C={C}/>
            <div aria-live="polite" data-pending style={{ fontSize: 15, color: C.text, minWidth: 0, maxWidth: "65ch" }}>
              {pending.emergency && <Emergency text={pending.emergency} C={C}/>}
              {!pending.text && (pending.sources || []).length > 0 && (
                <div data-reading className="bamiq-pop" style={{ marginBottom: 8, fontSize: 12.5, color: C.textMid }}>{readingLine(pending.sources)}</div>
              )}
              {pending.text ? <StreamText text={pending.text} C={C}/> : null}
              {pending.notices.map((n, i) => <div key={i} style={{ fontSize: 12.5, color: C.textMid, fontStyle: "italic" }}>{n}</div>)}
              {(pending.proposals || []).map(p => <ProposalCard key={p.action_id} proposal={{ ...p, status: "drafting" }} messageId={null} iq={iq} C={C} playerName={playerNameFor(iq, p.player_id)}/>)}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, fontSize: 12.5, color: C.textMid }}>
                <span className="bamiq-orb-mini" aria-hidden="true"/>
                <span className="bamiq-shimmer" style={{ "--bamiq-dim": C.textMid }}>{stageText(pending.stage)}</span>
                <button type="button" onClick={iq.stop} style={{ ...btn(C, false), padding: "3px 9px", fontSize: 12 }}>Stop</button>
              </div>
            </div>
          </>
        )}
        {interrupted && (
          <>
            <CoachBubble body={interrupted.coach} C={C}/>
            {interrupted.text && (
              <div style={{ fontSize: 15, color: C.textMid, minWidth: 0, maxWidth: "65ch" }}>{renderMarkdown(interrupted.text, { styles: READING, renderTag: plainTag(C), renderCard: cardRenderer(C) })}</div>
            )}
            <div role={interrupted.stopped ? "status" : "alert"} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 13, color: C.textMid }}>
              <span>{interrupted.reason}</span>
              {interrupted.retry && <button type="button" onClick={interrupted.retry} style={{ ...btn(C, false), padding: "4px 10px", fontSize: 12.5 }}>Try again</button>}
            </div>
          </>
        )}
        {iq.consentAsk && (
          <>
            {iq.consentAsk.coach && <CoachBubble body={iq.consentAsk.coach} attachments={iq.consentAsk.attachments} C={C}/>}
            <ConsentCard iq={iq} C={C}/>
          </>
        )}
        {error && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgHover, fontSize: 13.5, color: C.text }}>{error.text}</div>
        )}
      </div>

      {away && (messages.length > 0 || pending) && (
        <button type="button" onClick={jump} data-jump
          style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: compact ? 150 : 160, zIndex: 2, display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 999, border: `1px solid ${C.border}`, background: C.bgCard, boxShadow: C.elevatedShadow, color: C.text, fontFamily: ff, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
          <ArrowDown size={13}/> {pending ? "Jump to the answer" : "Jump to latest"}
        </button>
      )}

      <form onSubmit={(e) => { e.preventDefault(); submit(); }} style={{ padding: compact ? "10px 14px 12px" : "12px 28px 18px" }}>
        <UsageLine usage={iq.usage} C={C}/>
        {atLimit ? <LimitCard usage={iq.usage} C={C}/> : (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 16, background: C.inputBg, padding: "8px 8px 8px 12px", boxShadow: "0 1px 0 rgba(0,0,0,.04)" }}>
            <AttachBar iq={iq} page={page} C={C}/>
            <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
              <label htmlFor="bamiq-input" style={{ position: "absolute", left: -9999 }}>Ask BAM IQ</label>
              <textarea id="bamiq-input" ref={inputRef} value={draft} rows={1} disabled={!!pending}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) { e.preventDefault(); submit(); }
                  else if (e.key === "ArrowUp" && !draft && lastQuestion) { e.preventDefault(); setDraft(lastQuestion); }
                }}
                placeholder={pending ? "BAM IQ is answering…" : dictation.listening ? "Listening…" : "Ask a coaching question…"}
                style={{ flex: 1, resize: "none", padding: "7px 0", border: "none", background: "transparent",
                  color: C.text, fontFamily: ff, fontSize: 15, lineHeight: 1.45, outline: "none", minHeight: 34 }}/>
              {dictation.supported && (
                <button type="button" aria-label={dictation.listening ? "Stop dictating" : "Dictate"} aria-pressed={dictation.listening} disabled={!!pending}
                  title={dictation.listening ? "Stop dictating" : "Dictate: your browser turns what you say into text"}
                  onClick={() => (dictation.listening ? dictation.stop() : dictation.start(draft))}
                  className={dictation.listening ? "bamiq-listening" : undefined}
                  style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 999, border: "none", flexShrink: 0, cursor: "pointer",
                    background: dictation.listening ? "#E57373" : "transparent", color: dictation.listening ? "#111" : C.textMid }}>
                  {dictation.listening ? <Square size={13} fill="#111"/> : <Mic size={17}/>}
                </button>
              )}
              <button type="submit" aria-label="Send" disabled={!draft.trim() || !!pending}
                style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 999, border: "none", flexShrink: 0,
                  background: GOLD, color: "#111", cursor: !draft.trim() || pending ? "default" : "pointer", opacity: !draft.trim() || pending ? 0.4 : 1, transition: "opacity .15s ease" }}>
                <ArrowUp size={18} strokeWidth={2.5}/>
              </button>
            </div>
          </div>
        )}
        {dictation.error && <div role="alert" style={{ marginTop: 6, fontSize: 11.5, color: "#E57373" }}>{dictation.error}</div>}
        <div style={{ marginTop: 7, fontSize: 11, lineHeight: 1.45, color: C.textMid }}>BAM IQ is an AI coaching assistant, not Coleman, and it can be wrong. It sees a player's plan only if you attach it, and only you can save changes to it.</div>
      </form>
    </div>
  );
}
